/**
 * The dark code card used on /about and /api.
 *
 * It is always dark regardless of theme: a terminal reads as a terminal, and
 * the pages use it as a deliberate tonal break from the warm paper surface.
 */
import React, { useCallback, useMemo, useState } from 'react';
import { Platform, Pressable, ScrollView, View } from 'react-native';
import * as Clipboard from 'expo-clipboard';

import Icon from '@/components/Icon';
import Text from '@/components/Typography/Text';
import highlight from '@/constants/utils/highlight';

import { Chip, useLanding } from './LandingKit';
import { MONO, isWeb, labelType } from './landingTokens';

/** Muted against the near-black card, so the code reads before the colour does. */
const CODE_COLORS = {
  kw: '#C084FC',
  fn: '#7DD3FC',
  str: '#6EE7B7',
  num: '#FBBF24',
  com: 'rgba(247,245,241,0.36)',
  p: 'rgba(247,245,241,0.70)',
};

const SURFACE = '#0D0C0B';
const HAIRLINE = 'rgba(247,245,241,0.10)';
const DIM = 'rgba(247,245,241,0.56)';

function CodeLines({ lines, size, wrap }) {
  const leading = Math.round(size * 1.62);

  /**
   * Indentation is the difference between a code sample and a paragraph of
   * monospaced text, and HTML collapses leading whitespace by default. `pre`
   * keeps it; `pre-wrap` keeps it while still allowing long lines to break.
   */
  const preserveSpace = isWeb ? { whiteSpace: wrap ? 'pre-wrap' : 'pre' } : {};
  const lineStyle = { fontFamily: MONO, fontSize: size, lineHeight: leading, ...preserveSpace };

  return (
    <View>
      {lines.map((segments, lineIndex) => (
        // eslint-disable-next-line react/no-array-index-key
        <View key={`line-${lineIndex}`} style={{ minHeight: leading, justifyContent: 'center' }}>
          {segments.length ? (
            <Text numberOfLines={wrap ? undefined : 1} style={lineStyle}>
              {segments.map((segment, segmentIndex) => (
                <Text
                  // eslint-disable-next-line react/no-array-index-key
                  key={`seg-${lineIndex}-${segmentIndex}`}
                  style={{ ...lineStyle, color: CODE_COLORS[segment.c] || CODE_COLORS.p }}
                >
                  {segment.t}
                </Text>
              ))}
            </Text>
          ) : null}
        </View>
      ))}
    </View>
  );
}

function CopyButton({ code }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    if (Platform.OS === 'web') await navigator.clipboard?.writeText(code);
    else await Clipboard.setStringAsync(code);
    setCopied(true);
    const timer = setTimeout(() => setCopied(false), 1800);
    return () => clearTimeout(timer);
  }, [code]);

  return (
    <Pressable
      onPress={handleCopy}
      accessibilityRole="button"
      accessibilityLabel={copied ? 'Copied' : 'Copy code'}
      style={({ hovered }) => ({
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        paddingHorizontal: 10,
        paddingVertical: 5,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: HAIRLINE,
        backgroundColor: hovered ? 'rgba(247,245,241,0.09)' : 'transparent',
        ...(isWeb ? { cursor: 'pointer', transition: 'background-color 160ms linear' } : {}),
      })}
    >
      <Icon name={copied ? 'check' : 'copy'} size={10} color={DIM} solid />
      <Text weight="bold" style={{ color: DIM, ...labelType(9.5, 0.16) }}>
        {copied ? 'Copied' : 'Copy'}
      </Text>
    </Pressable>
  );
}

/**
 * `label` names the language in the title bar; `endpoint` puts the route being
 * demonstrated on the right. `tabs` turns the title bar into a language
 * switcher — pass `[{ id, label, language, code, endpoint }]` instead of `code`.
 */
export default function CodePanel({
  code,
  language = 'python',
  label,
  endpoint,
  tabs,
  copyable = true,
  wrap = false,
  size,
  style,
}) {
  const { c, isXS } = useLanding();
  const [activeId, setActiveId] = useState(tabs?.[0]?.id);

  const active = tabs
    ? (tabs.find((tab) => tab.id === activeId) || tabs[0])
    : { code, language, label: label || language, endpoint };

  const codeSize = size ?? (isXS ? 10.5 : 11.5);
  const lines = useMemo(
    () => highlight(active.code, active.language),
    [active.code, active.language],
  );

  return (
    <View
      style={[
        {
          borderRadius: 20,
          overflow: 'hidden',
          borderWidth: 1,
          borderColor: c.isDark ? HAIRLINE : 'rgba(20,17,14,0.4)',
          backgroundColor: SURFACE,
          ...(isWeb ? { boxShadow: c.shadowSoft } : {}),
        },
        style,
      ]}
    >
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: tabs ? 4 : 10,
          paddingHorizontal: tabs ? 8 : 16,
          paddingVertical: tabs ? 7 : 13,
          borderBottomWidth: 1,
          borderBottomColor: HAIRLINE,
        }}
      >
        {tabs ? (
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={{ gap: 4, alignItems: 'center' }}
            style={{ flex: 1, minWidth: 0 }}
          >
            {tabs.map((tab) => {
              const isActive = tab.id === active.id;
              return (
                <Pressable
                  key={tab.id}
                  onPress={() => setActiveId(tab.id)}
                  accessibilityRole="tab"
                  {...(isWeb ? { 'aria-selected': isActive } : { accessibilityState: { selected: isActive } })}
                  accessibilityLabel={tab.label}
                  style={({ hovered }) => ({
                    paddingHorizontal: 12,
                    paddingVertical: 7,
                    borderRadius: 8,
                    backgroundColor: isActive
                      ? 'rgba(247,245,241,0.10)'
                      : hovered ? 'rgba(247,245,241,0.05)' : 'transparent',
                    ...(isWeb ? { cursor: 'pointer', transition: 'background-color 160ms linear' } : {}),
                  })}
                >
                  <Text
                    weight="bold"
                    style={{
                      color: isActive ? '#F7F5F1' : DIM,
                      ...labelType(9.5, 0.16),
                    }}
                  >
                    {tab.label}
                  </Text>
                </Pressable>
              );
            })}
          </ScrollView>
        ) : (
          <>
            <Icon name="code" size={11} color={DIM} solid />
            <Text weight="bold" style={{ color: DIM, flex: 1, minWidth: 0, ...labelType(9.5, 0.18) }}>
              {active.label}
            </Text>
          </>
        )}

        {active.endpoint && !isXS ? <Chip tone="onDark">{active.endpoint}</Chip> : null}
        {copyable ? <CopyButton code={active.code} /> : null}
      </View>

      <ScrollView
        horizontal={!wrap}
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={{ padding: isXS ? 16 : 22, minWidth: wrap ? undefined : '100%' }}
      >
        <CodeLines lines={lines} size={codeSize} wrap={wrap} />
      </ScrollView>
    </View>
  );
}
